/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { Grid, Stack, List, ListItem, ListItemIcon, IconButton, Chip } from "@mui/material";
import FormImput from "components/generals/FormInput";
import BloodPressureInput from "components/generals/BloodPressureInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikState } from "formik";
import { vitalsFormYUPValidation } from "utils/form.utils";
import { AssetInterface, HeaderEnum, MediaStatusEnum, PatientInterface, VitalsInterface, VitalsAssetRelationInterface } from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import Typography from "components/generals/Typography";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DatePicker from "components/generals/DatePicker";
import { CoreAPI_URLs } from "constants/http.const";
import Button from "components/generals/Button";
import { FormProps } from "../../interfaces";
import Box from "components/generals/Box";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { useTranslation } from "react-i18next";
import { Method } from "axios";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import PermissionWrapper from "components/generals/PermissionWrapper";
import PDFVitalsUploader from "components/generals/PDFVitalsUploader";
import FilesViewer from "components/generals/MedicalAssetViewer";

/**
 * Props for VitalsForm component.
 */
interface VitalsFormInterface extends FormProps<VitalsInterface> {
  fromProgressNote?: boolean;
  setSaveVitals?: Dispatch<SetStateAction<boolean>>;
  appointmentId?: string;
  readOnlyForm?: boolean;
  patient?: Partial<PatientInterface>;
}

/**
 * Interface for file attachments with upload status tracking.
 */
interface AttachedFile {
  file: File;
  assetId?: string;
  status: MediaStatusEnum;
  name: string;
}

/**
 * Comprehensive vital signs recording form with automatic calculations.
 * 
 * Features:
 * - Complete vital signs capture:
 *   - Blood pressure (systolic/diastolic with custom input)
 *   - Heart rate, respiratory rate
 *   - Temperature (F/C conversion)
 *   - Oxygen saturation
 *   - Weight, height
 *   - BMI (automatic calculation)
 * - Intelligent BMI calculations:
 *   - Auto-calculate BMI from weight and height
 *   - Calculate weight from BMI and height
 *   - Calculate height from BMI and weight
 * - PDF vitals document upload with data extraction
 * - Multiple file attachment support with status tracking
 * - Medical asset viewer for attached documents
 * - Integration with MidMark device for automatic vital capture
 * - Date/time picker for vital measurement timestamp
 * - Form validation with Yup schema
 * - Read-only mode for viewing historical vitals
 * - Integration with progress notes workflow
 * - Appointment context awareness
 * - Permission-based save/delete actions
 * 
 * Calculation Logic:
 * - BMI = (weight_lb / (height_in²)) × 703
 * - Weight = (BMI × height_in²) / 703
 * - Height = √((weight_lb × 703) / BMI)
 * 
 * @param model - Vitals data to edit (or new vitals template)
 * @param action - Form action (NEW, MODIFY, READ_ONLY)
 * @param fromProgressNote - Flag indicating form opened from progress note
 * @param setSaveVitals - Callback to trigger save from parent component
 * @param appointmentId - Associated appointment ID
 * @param readOnlyForm - Force read-only mode
 * @param patient - Patient context for vital recording
 * @returns Vitals recording form with calculations
 */
const VitalsForm: React.FC<VitalsFormInterface> = ({
  model,
  action,
  fromProgressNote,
  setSaveVitals,
  appointmentId,
  readOnlyForm,
  patient,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [pdfUploadedData, setPdfUploadedData] = useState<Partial<VitalsInterface> | null>(null);
  const [vitalsAssets, setVitalsAssets] = useState<AssetInterface[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<AssetInterface | null>(null);
  const [formValues, setFormValues] = useState<Partial<VitalsInterface>>(model || {});

  const formikRef = useRef(null);

  const calculateBMI = (weight: number, height: number): number => {
    if (!weight || !height || weight <= 0 || height <= 0) return 0;
    return Number(((weight / (height * height)) * 703).toFixed(2));
  };

  const calculateWeightFromBMI = (bmi: number, height: number): number => {
    if (!bmi || !height || bmi <= 0 || height <= 0) return 0;
    return Number(((bmi * height * height) / 703).toFixed(2));
  };

  const calculateHeightFromBMI = (bmi: number, weight: number): number => {
    if (!bmi || !weight || bmi <= 0 || weight <= 0) return 0;
    const heightInInches = Math.sqrt((weight * 703) / bmi);
    return Number(heightInInches.toFixed(2));
  };

  const processVitalsCalculations = (values: Partial<VitalsInterface>): Partial<VitalsInterface> => {
    const processedValues = { ...values };
    const weight = Number(processedValues.wt) || 0;
    const height = Number(processedValues.ht) || 0;
    const bmi = Number(processedValues.bmi) || 0;

    if (weight > 0 && height > 0) {
      const calculatedBMI = calculateBMI(weight, height);
      if (Math.abs(calculatedBMI - bmi) > 0.01) {
        processedValues.bmi = calculatedBMI;
      }
    }
    else if (bmi > 0 && height > 0 && weight === 0) {
      processedValues.wt = calculateWeightFromBMI(bmi, height);
    }
    else if (bmi > 0 && weight > 0 && height === 0) {
      processedValues.ht = calculateHeightFromBMI(bmi, weight);
    }
    else if (bmi > 0 && weight > 0 && height === 0) {
      processedValues.ht = calculateHeightFromBMI(bmi, weight);
    }

    return processedValues;
  };

  useEffect(() => {
    if (model && Object.keys(formValues).length === 0) {
      const processedModel = processVitalsCalculations(model);
      setFormValues(processedModel);
    }
  }, [model]);

  const generateUniqueFileName = (originalName: string, existingNames: string[]): string => {
    const lastDotIndex = originalName.lastIndexOf('.');
    const nameWithoutExtension = lastDotIndex > 0 ? originalName.substring(0, lastDotIndex) : originalName;
    const extension = lastDotIndex > 0 ? originalName.substring(lastDotIndex) : '';
    
    if (!existingNames.includes(originalName)) {
      return originalName;
    }
    
    let counter = 1;
    let newName = `${nameWithoutExtension} (${counter})${extension}`;
    
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${nameWithoutExtension} (${counter})${extension}`;
    }
    
    return newName;
  };

  const generateUniqueFileNameForSave = (
    originalName: string, 
    patientFirstName: string, 
    patientLastName: string, 
    currentDate: string,
    existingAssets: AssetInterface[]
  ): string => {
    const nameWithoutExtension = originalName.replace(/\.[^/.]+$/, "");
    const baseFileName = `${nameWithoutExtension}_${patientFirstName}_${patientLastName}_${currentDate}.pdf`;
    
    const existingNames = existingAssets.map(asset => asset.orgName);
    
    return generateUniqueFileName(baseFileName, existingNames);
  };

  const fetchVitalsAssets = async (vitalsId: string) => {
    if (!vitalsId) return;
  
    try {
      sendRequest<VitalsAssetRelationInterface[]>(
        CoreAPI_URLs.VITALS_ASSET_RELATION,
        (response) => {
          if (response && Array.isArray(response)) {
            const filteredRelations = response.filter(relation => {
              return relation.vitals?.id === vitalsId;
            });
            
            const assets = filteredRelations
              .map(relation => relation.asset)
              .filter(asset => asset && asset.id) as AssetInterface[];
            
            setVitalsAssets(assets);
          } else {
            setVitalsAssets([]);
          }
        },
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: vitalsId }]
      );
    } catch (error) {
      console.error("Error fetching vitals assets:", error);
    }
  };

  useEffect(() => {
    if (model?.id) {
      fetchVitalsAssets(model.id);
    }
  }, [model?.id]);

  const prepareModelForSubmission = (model: Partial<VitalsInterface>): Partial<VitalsInterface> => {
    const cleanedModel = { ...model };
    
    Object.keys(cleanedModel).forEach(key => {
      const value = cleanedModel[key as keyof VitalsInterface];
      
      if (key === 'bp' || key === 'note' || key === 'registrationDate') {
        return;
      }
      
      if (typeof value === 'string' && value.trim() !== '') {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          (cleanedModel as any)[key] = numValue;
        }
      }
      
      if (typeof value === 'string' && value.trim() === '') {
        (cleanedModel as any)[key] = undefined;
      }
    });

    if (!cleanedModel.painScale) {
      cleanedModel.painScale = 0;
    }

    return cleanedModel;
  };

  const submitHandler = async (model: Partial<VitalsInterface>) => {
    let method: Method;
    let message: string;
    let resourceId: string;

    const modelToSend = prepareModelForSubmission(model);
    
    if (appointmentId) {
      if (action === FormActionType.NEW) {
        method = "POST";
        message = t("vitalsForm.succesfullyCreatedVitals");
        resourceId = appointmentId;
      } else if (action === FormActionType.MODIFY) {
        method = "PUT";
        message = t("vitalsForm.succesfullyUpdatedVitals");
        resourceId = modelToSend.id;
      } 
      
      sendRequest(
        CoreAPI_URLs.VITALS_GENERAL_OPERATION,
        async (vitalsResponse) => {       
          if (vitalsResponse && patient?.id) {
            const pendingFiles = attachedFiles.filter(file => file.status === MediaStatusEnum.PENDING);     
            for (const fileData of pendingFiles) {
              await savePdfToVitalsHistory(fileData.file, patient.id, fileData, vitalsResponse);
            }
          }
        },
        method,
        JSON.stringify(modelToSend),
        [{ header: HeaderEnum.RESOURCE_ID, value: resourceId }],
        {successMessage: message}  
      );
    }
  };

  useEffect(() => {
    if (fromProgressNote && setSaveVitals) {
      const currentFormValues = formikRef.current?.values;
      if (currentFormValues && appointmentId) {
        submitHandler(currentFormValues);
      }
      setSaveVitals(false);
    }
  }, [fromProgressNote, setSaveVitals, appointmentId]);

  const createRenamedFile = async (originalFile: File, newFileName: string): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function(e) {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const newFile = new File([arrayBuffer], newFileName, {
          type: originalFile.type,
          lastModified: originalFile.lastModified,
        });
        resolve(newFile);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(originalFile);
    });
  };
  
  const cleanFileName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
  };

  const savePdfToVitalsHistory = async (pdfFile: File, patientId: string, fileData: AttachedFile, vitalsResponse?: Partial<VitalsInterface>) => {
    try {
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  
      const patientFirstName = cleanFileName(patient?.person?.name || 'Unknown');
      const patientLastName = cleanFileName(patient?.person?.lastname || 'Patient');
  
      const uniqueFileName = generateUniqueFileNameForSave(
        pdfFile.name,
        patientFirstName,
        patientLastName,
        currentDate,
        vitalsAssets
      );

      const renamedFile = await createRenamedFile(pdfFile, uniqueFileName);
      
      const assetData = {
        mimetype: renamedFile.type,
        source: "Other",
        category: "Other",
        orgName: renamedFile.name,
        status: MediaStatusEnum.PENDING,
      };
      
      sendRequest<AssetInterface>(
        CoreAPI_URLs.ASSETS,
        (assetResponse) => { 
          if (assetResponse && assetResponse.id) {
            setAttachedFiles(prev => 
              prev.map(file => 
                file.name === fileData.name 
                  ? { ...file, assetId: assetResponse.id }
                  : file
              )
            );
  
            const formData = new FormData();
            formData.append("file", renamedFile);
            sendRequest<AssetInterface>(
              CoreAPI_URLs.ASSETS_UPLOAD,
              (uploadResponse) => {         
                if (uploadResponse && uploadResponse.id) {
                  const patientRelationData = {
                    patient: { id: patientId },
                    asset: { id: uploadResponse.id },
                    customCategory: "Vitals History",
                  };
                  
                  sendRequest(
                    CoreAPI_URLs.ASSET_PATIENT_RELATION,
                    () => {
                      if (vitalsResponse?.id) {
                        const vitalsRelationData = {
                          vitals: { id: vitalsResponse.id },
                          asset: { id: uploadResponse.id },
                          customCategory: "Vitals History",
                        };
                        
                        sendRequest(
                          CoreAPI_URLs.VITALS_ASSET_RELATION,
                          () => {
                            setAttachedFiles(prev => 
                              prev.map(file => 
                                file.name === fileData.name 
                                  ? { ...file, status: MediaStatusEnum.UPLOADED }
                                  : file
                              )
                            );
                            
                            setVitalsAssets(prev => [...prev, uploadResponse]);
                          },
                          "POST",
                          JSON.stringify(vitalsRelationData)
                        );
                      } else {
                        setAttachedFiles(prev => 
                          prev.map(file => 
                            file.name === fileData.name 
                              ? { ...file, status: MediaStatusEnum.UPLOADED }
                              : file
                          )
                        );
                      }
                    },
                    "POST",
                    JSON.stringify(patientRelationData)
                  );
                } else {
                  setAttachedFiles(prev => 
                    prev.map(file => 
                      file.name === fileData.name 
                        ? { ...file, status: MediaStatusEnum.ERROR }
                        : file
                    )
                  );
                }
              },
              "POST",
              formData,
              [{ header: HeaderEnum.RESOURCE_ID, value: assetResponse.id }]
            );
          } else {
            setAttachedFiles(prev => 
              prev.map(file => 
                file.name === fileData.name 
                  ? { ...file, status: MediaStatusEnum.ERROR }
                  : file
            )
          );
          }
        },
        "POST",
        JSON.stringify(assetData)
      );
    } catch (error) {
      setAttachedFiles(prev => 
        prev.map(file => 
          file.name === fileData.name 
            ? { ...file, status: MediaStatusEnum.ERROR }
            : file
        )
      );
    }
  };

  const handleDataImported = (importedData: Partial<VitalsInterface> | any) => {
    if (formikRef.current) {
      const currentValues = formikRef.current.values;
      const processedData = processVitalsCalculations({ ...currentValues, ...importedData });
      
      formikRef.current.setValues(processedData);
      setFormValues(processedData);
    }
  };

  const handlePdfDataExtracted = (extractedData: Partial<VitalsInterface>, pdfFile: File) => {
    setPdfUploadedData(extractedData);
    handleDataImported(extractedData);
    
    const attachedFileNames = attachedFiles.map(file => file.name);
    const uniqueFileName = generateUniqueFileName(pdfFile.name, attachedFileNames);
    
    const fileToAdd = uniqueFileName === pdfFile.name ? pdfFile : new File([pdfFile], uniqueFileName, {
      type: pdfFile.type,
      lastModified: pdfFile.lastModified,
    });
    
    const newAttachedFile: AttachedFile = {
      file: fileToAdd,
      status: MediaStatusEnum.PENDING,
      name: uniqueFileName
    };
    
    setAttachedFiles(prev => [...prev, newAttachedFile]);
  };

  const handleRemoveFile = (fileName: string) => {
    setAttachedFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const handleAssetClick = (asset: AssetInterface) => {
    setSelectedAsset(asset);
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return <PictureAsPdfIcon />;
    }
    return <AttachFileIcon />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case MediaStatusEnum.UPLOADED:
        return 'success';
      case MediaStatusEnum.ERROR:
        return 'error';
      case MediaStatusEnum.PENDING:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case MediaStatusEnum.UPLOADED:
        return t("vitalsForm.uploaded");
      case MediaStatusEnum.ERROR:
        return t("vitalsForm.error");
      case MediaStatusEnum.PENDING:
      default:
        return t("vitalsForm.pending");
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (formikRef.current) {
      const numericFields = ['hr', 'rr', 'temp', 'oxy', 'wt', 'ht', 'bmi', 'wtPercentage', 'htPercentage', 'bmiPercentage', 'painScale'];
      
      if (numericFields.includes(fieldName)) {
        if (!value) {
          formikRef.current.setFieldValue(fieldName, '');
        } else {
          formikRef.current.setFieldValue(fieldName, value);
        }
      } else {
        formikRef.current.setFieldValue(fieldName, value);
      }
    }
  };
  
  const handleFieldBlur = (fieldName: string) => {
    if (formikRef.current && ['wt', 'ht', 'bmi'].includes(fieldName)) {
      const currentValues = formikRef.current.values;
      const weight = Number(currentValues.wt) || 0;
      const height = Number(currentValues.ht) || 0;
      const bmi = Number(currentValues.bmi) || 0;
  
      let shouldRecalculate = false;
      const processedValues = { ...currentValues };
  
      if (fieldName === 'wt' || fieldName === 'ht') {
        if (weight > 0 && height > 0) {
          const calculatedBMI = calculateBMI(weight, height);
          if (Math.abs(calculatedBMI - bmi) > 0.01) {
            processedValues.bmi = calculatedBMI;
            shouldRecalculate = true;
          }
        }
      } else if (fieldName === 'bmi') {
        if (bmi === 0) {
          if (weight > 0 && height > 0) {
            processedValues.bmi = calculateBMI(weight, height);
            shouldRecalculate = true;
          }
        } else if (bmi > 0) {
          if (height > 0 && weight === 0) {
            processedValues.wt = calculateWeightFromBMI(bmi, height);
            shouldRecalculate = true;
          } else if (weight > 0 && height === 0) {
            processedValues.ht = calculateHeightFromBMI(bmi, weight);
            shouldRecalculate = true;
          }
        }
      }
  
      if (shouldRecalculate) {
        formikRef.current.setValues(processedValues);
        setFormValues(processedValues);
      }
    }
  };

  return (
    <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">       
      <FormikForm
        innerRef={formikRef}
        onSubmit={(model) => {
          submitHandler(model);
        }}
        initialValues={formValues}
        validationSchema={vitalsFormYUPValidation(t)}
        enableReinitialize={true}
      >
        {({ values, errors, touched}: FormikState<Partial<VitalsInterface>>) => {
          return (
          <Form id="vitalsForm" autoComplete="true">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <FolderSharedIcon fontSize="large" />
              <Typography variant="h4">{t("vitalsForm.vitalsForm")}</Typography>
            </Stack>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6} lg={4}>
                {readOnlyForm ? (
                  <FormImput
                    name="rd"
                    label={t("vitalsForm.registrationDate")}
                    value={
                      values.registrationDate
                        ? new Date(values.registrationDate).toLocaleString()
                        : ""
                    }
                    readOnly={readOnlyForm}
                    variant="outlined"
                  />
                ) : (
                  <DatePicker
                    id="cy-registrationDate-picker"
                    name="registrationDate"
                    label={t("vitalsForm.registrationDate")}
                    value={values.registrationDate}
                    error={errors.registrationDate && touched.registrationDate}
                    options={{
                      enableTime: true,
                      dateFormat: "Y-m-d H:i",
                    }}
                    onChange={(selectedDate) => {
                      formikRef.current?.setFieldValue("registrationDate", selectedDate[0]);
                    }}
                    disabled={false}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <BloodPressureInput
                  name="bp"
                  label={t("vitalsForm.bloodPressure")}
                  readOnly={readOnlyForm}
                  error={errors.bp && touched.bp}
                  placeholder="120/80"
                />
              </Grid>
              
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.heartRate")}
                  name="hr"
                  id="hr"
                  value={values.hr || ""}
                  error={errors.hr && touched.hr}
                  onChange={(e) => handleFieldChange('hr', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.respirationRate")}
                  name="rr"
                  id="rr"
                  value={values.rr || ""}
                  error={errors.rr && touched.rr}
                  onChange={(e) => handleFieldChange('rr', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.temperature")}
                  name="temp"
                  id="temp"
                  value={values.temp || ""}
                  error={errors.temp && touched.temp}
                  onChange={(e) => handleFieldChange('temp', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.oxygenSaturation")}
                  name="oxy"
                  id="oxy"
                  value={values.oxy || ""}
                  error={errors.oxy && touched.oxy}
                  onChange={(e) => handleFieldChange('oxy', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.weight")}
                  name="wt"
                  id="wt"
                  value={values.wt || ""}
                  error={errors.wt && touched.wt}
                  onChange={(e) => handleFieldChange('wt', e.target.value)}
                  onBlur={() => handleFieldBlur('wt')}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.wtPercentage")}
                  name="wtPercentage"
                  id="wtPercentage"
                  value={values.wtPercentage || ""}
                  error={errors.wtPercentage && touched.wtPercentage}
                  onChange={(e) => handleFieldChange('wtPercentage', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.height")}
                  name="ht"
                  id="ht"
                  value={values.ht || ""}
                  error={errors.ht && touched.ht}
                  onChange={(e) => handleFieldChange('ht', e.target.value)}
                  onBlur={() => handleFieldBlur('ht')}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.htPercentage")}
                  name="htPercentage"
                  id="htPercentage"
                  value={values.htPercentage || ""}
                  error={errors.htPercentage && touched.htPercentage}
                  onChange={(e) => handleFieldChange('htPercentage', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.bodyMassIndex")}
                  name="bmi"
                  id="bmi"
                  value={values.bmi || ""}
                  error={errors.bmi && touched.bmi}
                  onChange={(e) => handleFieldChange('bmi', e.target.value)}
                  onBlur={() => handleFieldBlur('bmi')}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.bmiPercentage")}
                  name="bmiPercentage"
                  id="bmiPercentage"
                  value={values.bmiPercentage || ""}
                  error={errors.bmiPercentage && touched.bmiPercentage}
                  onChange={(e) => handleFieldChange('bmiPercentage', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="number"
                  label={t("vitalsForm.painScale")}
                  name="painScale"
                  id="painScale"
                  value={values.painScale || ""}
                  error={errors.painScale && touched.painScale}
                  onChange={(e) => handleFieldChange('painScale', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={8}>
                <FormImput
                  readOnly={readOnlyForm}
                  type="textarea"
                  label={t("vitalsForm.notes")}
                  name="note"
                  id="note"
                  multiline
                  rows={3}
                  value={values.note || ""}
                  error={errors.note && touched.note}
                />
              </Grid>
              
              
              {!readOnlyForm && (
                <Grid item xs={12}>
                  <Box mb={3} display="flex" justifyContent="flex-end" gap={2}>
                    <PDFVitalsUploader 
                      onDataExtracted={handlePdfDataExtracted}
                      appointmentId={appointmentId}
                      disabled={!appointmentId}
                    />
                  </Box>
                  {attachedFiles.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="h6" mb={2}>
                        {t("vitalsForm.attachedFiles")}
                      </Typography>
                      <List>
                        {attachedFiles.map((file, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: file.status === MediaStatusEnum.UPLOADED ? '#f0f8f0' : 'transparent'
                            }}
                          >
                            <ListItemIcon>
                              {getFileIcon(file.name)}
                            </ListItemIcon>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {file.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={getStatusText(file.status)}
                                color={getStatusColor(file.status)}
                                icon={file.status === MediaStatusEnum.UPLOADED ? <CheckCircleIcon /> : null}
                              />
                            </Box>
                            <IconButton
                              onClick={() => handleRemoveFile(file.name)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Grid>
              )}
              {vitalsAssets.length > 0 && (
                <Grid item xs={12}>
                  <Box mb={3}>
                    <Typography variant="h6" mb={3}>
                      {t("vitalsForm.vitalsDocuments")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List>
                          {vitalsAssets.map((asset, index) => (
                            <ListItem
                              key={index}
                              onClick={() => handleAssetClick(asset)}
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                mb: 1,
                                cursor: 'pointer',
                                backgroundColor: selectedAsset?.id === asset.id ? '#f0f8f0' : 'transparent',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5'
                                }
                              }}
                            >
                              <ListItemIcon>
                                {getFileIcon(asset.orgName)}
                              </ListItemIcon>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {asset.orgName}
                                </Typography>
                                <Typography variant="caption" color="secondary">
                                  {asset.mimetype}
                                </Typography>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {selectedAsset ? (
                          <FilesViewer key={selectedAsset.id} asset={selectedAsset} />
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              height: '200px',
                              border: '1px dashed #ccc',
                              borderRadius: 1,
                              backgroundColor: '#fafafa'
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              color="secondary"
                              textAlign="center"
                            >
                              {t("vitalsForm.selectFileToPreview")}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}

              {!readOnlyForm && (
                <Grid item>
                  <PermissionWrapper
                    requiredPermissions={[
                      PermissionsUser[EntityPermissions.VITALS_PAGE].CREATE,
                      PermissionsUser[EntityPermissions.VITALS_PAGE].EDIT,
                    ]}
                  >
                    <Button type="submit" color="success" id="cy-save-vitals">
                      {t("vitalsForm.saveVitals")}
                    </Button>
                  </PermissionWrapper>
                </Grid>
              )}
            </Grid>
          </Form>
              );
            }}
        </FormikForm>
    </Box>
  );
};

export default VitalsForm;